import { useAuthorizedQuery } from "../api/authorized-query";
import { seedgreenAPI } from "../api/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
/**
 * Query to get all invites for the current user's customer
 */
export function useInvites(createdAfter) {
    const isAuthorized = useAuthorizedQuery();
    return useQuery({
        queryKey: ["invites", createdAfter],
        queryFn: async () => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.get("v2/invite", {
                params: {
                    ...(createdAfter && {
                        createdAfter: createdAfter.toISOString(),
                    }),
                },
            });
            return response.data;
        },
        enabled: isAuthorized,
    });
}
/**
 * Query to get signup invite information during signup flow.
 * Uses the standard API instance since the endpoint allows both authenticated and anonymous requests.
 */
export function useSignupInvite(token) {
    return useQuery({
        queryKey: ["signup-invite", token],
        queryFn: async () => {
            if (!token.value) {
                throw new Error("Token is required");
            }
            const response = await seedgreenAPI.get(`v2/invite/signup/${token.value}`);
            return response.data;
        },
        enabled: computed(() => !!token.value),
        staleTime: 0, // Don't cache signup invite results
        gcTime: 0, // Don't keep in cache after component unmounts
        retry: false, // Don't retry failed requests
    });
}
/**
 * Query to get a single invite by ID
 * This uses the existing invites query and filters by ID to avoid unnecessary API calls
 */
export function useInvite(inviteId) {
    const { data: invites, isLoading, error } = useInvites();
    const invite = computed(() => {
        if (!invites.value || !inviteId.value)
            return null;
        return invites.value.find((inv) => inv.id === inviteId.value) || null;
    });
    return {
        data: invite,
        isLoading,
        error,
    };
}
/**
 * Mutation to create a single invite
 */
export function useCreateInvite() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (inviteData) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("v2/invite", inviteData);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate invites queries to refetch the list
            queryClient.invalidateQueries({
                queryKey: ["invites"],
            });
        },
    });
    return {
        createInvite: mutateAsync,
        isLoading: isPending,
        error,
    };
}
/**
 * Mutation to create multiple invites in bulk
 */
export function useCreateBulkInvites() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (invites) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            const response = await seedgreenAPI.post("v2/invite/bulk", invites);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate invites queries to refetch the list
            queryClient.invalidateQueries({
                queryKey: ["invites"],
            });
        },
    });
    return {
        createBulkInvites: mutateAsync,
        isLoading: isPending,
        error,
    };
}
/**
 * Mutation to resend an invite
 */
export function useResendInvite() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (inviteId) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            await seedgreenAPI.post(`v2/invite/${inviteId}/resend`);
        },
        onSuccess: () => {
            // Invalidate invites queries to refetch the list
            queryClient.invalidateQueries({
                queryKey: ["invites"],
            });
        },
    });
    return {
        resendInvite: mutateAsync,
        isLoading: isPending,
        error,
    };
}
/**
 * Mutation to cancel an invite
 */
export function useCancelInvite() {
    const queryClient = useQueryClient();
    const isAuthorized = useAuthorizedQuery();
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (inviteId) => {
            if (!isAuthorized.value)
                throw new Error("Not authorized");
            await seedgreenAPI.post(`v2/invite/${inviteId}/cancel`);
        },
        onSuccess: () => {
            // Invalidate invites queries to refetch the list
            queryClient.invalidateQueries({
                queryKey: ["invites"],
            });
        },
    });
    return {
        cancelInvite: mutateAsync,
        isLoading: isPending,
        error,
    };
}
/**
 * Mutation to accept an invite and create a new user account.
 * This is used during the signup flow and doesn't require authentication.
 */
export function useAcceptInvite() {
    const { mutateAsync, isPending, error } = useMutation({
        mutationFn: async (params) => {
            const response = await seedgreenAPI.post(`v2/invite/accept/${params.token}`, params.acceptData);
            return response.data;
        },
        // Note: We don't invalidate queries here since this is typically
        // followed by a redirect and the user won't be authenticated yet
    });
    return {
        acceptInvite: mutateAsync,
        isLoading: isPending,
        error,
    };
}
